// ── LA SCHEDA TECNICA DELL'ALLIEVO ───────────────────────────────────
// Com'è l'allievo in campo, oggi: il suo livello PTR, il tipo di giocatore
// FITP e le macroaree su cui ci si lavora, ognuna con le sue voci e una
// nota facoltativa. Le parole sono quelle del contenuto della lezione
// (lessonContent.utils), così il "rovescio" della scheda è lo stesso
// rovescio delle lezioni; la scala PTR è quella del CAD (func.utils).
//
// Si salvano le voci, come sulla lezione, e le note per macroarea: la
// prima scheda (#165) le aveva per voce, e si leggono unite nella loro
// macroarea.
//
// Niente voti per voce e niente storico: la scheda dice a che punto si è, e
// quello che si è fatto lo dicono le lezioni dell'allievo. Nessuna lezione
// la aggiorna da sola: la scrive solo `editPlayerTechnicalSheet`.

import { getPtrLevel } from "./func.utils"
import {
	FOCUS_AREAS,
	SITUATIONS,
	STROKES,
	chosenAreas,
	isAreaOn,
	playerStyleLabel,
} from "./lessonContent.utils"

// Quanto può essere lunga la nota di una macroarea: un appunto, non un diario.
export const FOCUS_NOTE_MAX_TEXT = 300

export const EMPTY_TECHNICAL_SHEET = {
	ptrLevel: null,
	playerStyle: null,
	strokes: [],
	situations: [],
	notes: {},
}

// Solo le voci del vocabolario, una volta sola, nell'ordine del vocabolario
const knownIDs = (list, ids) => {
	const chosen = new Set(Array.isArray(ids) ? ids : [])
	return list.filter(item => chosen.has(item.id)).map(item => item.id)
}

const noteText = note => (typeof note === "string" ? note : "")

// La nota di una macroarea: la sua, o quelle delle sue voci se la scheda
// è di quando le note erano per voce
const areaNote = (notes, area) =>
	// lo spostamento è insieme macroarea e voce: la sua nota una volta sola
	[...new Set([area.id, ...area.ids])]
		.map(key => noteText(notes[key]))
		.filter(note => note.trim() !== "")
		.join("\n")

// La scheda com'è salvata, o com'è nel foglio, ridotta a quello che vale:
// un livello della scala, un tipo che esiste, le voci conosciute e le note
// solo delle macroaree accese, senza spazi intorno. Una nota vuota non si
// tiene, e spegnendo una macroarea se ne va anche la sua nota. Un allievo
// di prima non ha il campo, e si legge come una scheda vuota.
export const normalizeTechnicalSheet = sheet => {
	const strokes = knownIDs(STROKES, sheet?.strokes)
	const situations = knownIDs(SITUATIONS, sheet?.situations)
	const notes = sheet?.notes ?? {}

	return {
		ptrLevel: getPtrLevel(sheet?.ptrLevel) ? sheet.ptrLevel : null,
		playerStyle: playerStyleLabel(sheet?.playerStyle) ? sheet.playerStyle : null,
		strokes,
		situations,
		notes: Object.fromEntries(
			FOCUS_AREAS.filter(area => isAreaOn({ strokes, situations }, area))
				.map(area => [area.id, areaNote(notes, area).trim().slice(0, FOCUS_NOTE_MAX_TEXT)])
				.filter(([, note]) => note !== ""),
		),
	}
}

export const isTechnicalSheetEmpty = sheet => {
	const normalized = normalizeTechnicalSheet(sheet)
	return (
		normalized.ptrLevel === null &&
		!normalized.playerStyle &&
		normalized.strokes.length === 0 &&
		normalized.situations.length === 0
	)
}

// Le macroaree su cui si lavora, nell'ordine di sempre, ognuna col suo
// nome, il dettaglio delle voci se non sono tutte, e la sua nota: è la
// lista di "Ci lavoriamo". `kind` dice di che colore è la pastiglia.
export const focusItems = sheet => {
	const notes = sheet?.notes ?? {}
	return chosenAreas(sheet).map(({ area, detail }) => ({
		id: area.id,
		kind: area.list === "situations" ? "situation" : "stroke",
		label: area.label,
		detail,
		note: noteText(notes[area.id]),
	}))
}
